import "./App.css";

import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router";

import { AuthenticatedApolloProvider } from "./components/AuthenticatedApolloProvider/AuthenticatedApolloProvider";
import { ErrorWrapper } from "./components/Errors/ErrorWrapper";
import { LoadingScreen } from "./components/LoadingScreen/LoadingScreen";
import PlayerWrapper from "./components/PlayerWrapper/PlayerWrapper";
import { useAppLoadingState } from "./hooks/useAppLoadingState";
import { TelemetryAwaitingEmitter } from "./telemetry/TelemetryUtils";

const Authorizer = React.lazy(() => import("./pages/Authorizer/Authorizer").then(m => ({ default: m.Authorizer })));
const Library = React.lazy(() => import("./pages/Library/Library").then(m => ({ default: m.Library })));
const Player = React.lazy(() => import("./pages/Player/Player").then(m => ({ default: m.Player })));

function CourseRoute(): React.ReactElement {
  const { courseId } = useParams<{ courseId: string }>();

  if (!courseId || !/^\d+$/.test(courseId)) {
    return <Navigate to="/" replace />;
  }

  return (
    <AuthenticatedApolloProvider>
      <PlayerWrapper>
        <Suspense fallback={<LoadingScreen message="Loading..." />}>
          <Player />
        </Suspense>
      </PlayerWrapper>
    </AuthenticatedApolloProvider>
  );
}

function App(): React.ReactElement {
  const { isLoading, loadingMessage } = useAppLoadingState();

  const handleContextMenu = (event: MouseEvent) => {
    const urlParams = new URLSearchParams(window.location.search);
    const debug = urlParams.get("debug") === "true";
    const parent = document.getElementById("zopim-widget");
    const target = event.target as HTMLElement;

    if ((!parent || (parent && target && !parent.contains(target))) && !debug) {
      event.preventDefault();
    } else {
      return true;
    }
  };

  useEffect(() => {
    document.addEventListener("contextmenu", handleContextMenu);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, []);

  // Show loading state when isLoading is true
  if (isLoading) {
    return <LoadingScreen message={loadingMessage} />;
  }

  return (
    <div className="App" id="App" style={{ display: "flex" }}>
      <ErrorWrapper>
        <TelemetryAwaitingEmitter />
        <BrowserRouter>
          <Routes>
            <Route
              path="/"
              element={
                <AuthenticatedApolloProvider>
                  <PlayerWrapper>
                    <Suspense fallback={<LoadingScreen message="Loading..." />}>
                      <Library />
                    </Suspense>
                  </PlayerWrapper>
                </AuthenticatedApolloProvider>
              }
            />
            <Route
              path="/authorize"
              element={
                <Suspense fallback={<LoadingScreen message="Loading..." />}>
                  <Authorizer />
                </Suspense>
              }
            />
            <Route path="/:courseId" element={<CourseRoute />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </ErrorWrapper>
    </div>
  );
}

export default App;
